// Mobile-only UI configuration: the SyQi home dashboard's widgets and the bottom
// nav's tool slots. Both are ordered, toggleable lists the user arranges in
// Settings. These prefs don't exist on web, so they persist locally via
// AsyncStorage (keyed per user) rather than the shared user_settings table.
// Storage keys still use "oracle" prefix (oracle_widgets) for compatibility.
import { useCallback, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ALL_TOOL_IDS, TOOL_NAMES } from '../domain/olmps';

export interface OrderedItem {
  id: string;
  visible: boolean;
}

// ── Oracle dashboard widgets ───────────────────────────────────────────────
export interface WidgetDef {
  id: string;
  label: string;
  hint: string;
}
export const ORACLE_WIDGETS: WidgetDef[] = [
  { id: 'astra', label: 'ASTRA', hint: 'Your orbital view at a glance.' },
  { id: 'spectrum', label: 'Spectrum & scores', hint: 'Olympian and Mental Order, place your lines.' },
  { id: 'rings', label: 'CLEAR rings', hint: 'Tasks, Projects, and Filing progress.' },
  { id: 'today', label: 'Today', hint: "The day's run-down." },
];
// Stable module-level id list — pass this (not an inline .map) to hooks so the
// catalog reference doesn't change every render.
export const ORACLE_WIDGET_IDS = ORACLE_WIDGETS.map((w) => w.id);
export const DEFAULT_ORACLE_WIDGETS = ['astra', 'spectrum', 'rings', 'today'];

// ── Bottom nav tool slots ──────────────────────────────────────────────────
// Any OLMPS tool can occupy a slot; these three are the sensible defaults.
export const DEFAULT_BOTTOM_NAV = ['astrs', 'moyri', 'numa'];
export const MAX_BOTTOM_NAV = 3;

export function widgetLabel(id: string): string {
  return ORACLE_WIDGETS.find((w) => w.id === id)?.label ?? id;
}
export function bottomNavLabel(id: string): string {
  return TOOL_NAMES[id] ?? id;
}
export const BOTTOM_NAV_CATALOG = ALL_TOOL_IDS;

// ── Generic ordered/toggleable config backed by AsyncStorage ───────────────
function storageKey(column: string, userId: string | undefined): string {
  return `olmps:${column}:${userId ?? 'anon'}`;
}

export function useOrderedConfig(
  userId: string | undefined,
  column: string,
  catalog: string[],
  defaults: string[]
) {
  const [items, setItems] = useState<OrderedItem[] | null>(null);

  const buildDefaults = useCallback((): OrderedItem[] => {
    // Everything in the catalog is a row; defaults start visible, the rest hidden.
    const defaultSet = new Set(defaults);
    const head = defaults.map((id) => ({ id, visible: true }));
    const tail = catalog.filter((id) => !defaultSet.has(id)).map((id) => ({ id, visible: false }));
    return [...head, ...tail];
  }, [catalog, defaults]);

  const load = useCallback(async () => {
    const fallback = buildDefaults();
    try {
      const raw = await AsyncStorage.getItem(storageKey(column, userId));
      if (raw) {
        const parsed = JSON.parse(raw) as OrderedItem[];
        if (Array.isArray(parsed)) {
          const clean = parsed.filter((it) => it && typeof it.id === 'string' && catalog.includes(it.id));
          const known = new Set(clean.map((c) => c.id));
          // Append any catalog ids added since the pref was saved.
          catalog.forEach((id) => {
            if (!known.has(id)) clean.push({ id, visible: defaults.includes(id) });
          });
          setItems(clean);
          return;
        }
      }
    } catch {
      /* fall through to defaults */
    }
    setItems(fallback);
  }, [userId, column, catalog, defaults, buildDefaults]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(
    (next: OrderedItem[]) => {
      setItems([...next]);
      void AsyncStorage.setItem(storageKey(column, userId), JSON.stringify(next)).catch(() => {});
    },
    [userId, column]
  );

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      if (!items) return;
      persist(items.map((it) => (it.id === id ? { ...it, visible } : it)));
    },
    [items, persist]
  );

  const move = useCallback(
    (id: string, dir: -1 | 1) => {
      if (!items) return;
      const next = [...items];
      const idx = next.findIndex((i) => i.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= next.length) return;
      [next[idx], next[j]] = [next[j], next[idx]];
      persist(next);
    },
    [items, persist]
  );

  const ordered = useMemo(() => items ?? [], [items]);
  const visibleIds = useMemo(() => ordered.filter((i) => i.visible).map((i) => i.id), [ordered]);

  return { loading: items === null, items: ordered, visibleIds, setVisible, move };
}
